import { RefreshCw, ScrollText } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { COMPLETED_STATUS_STYLES } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate, formatTime } from '@/lib/utils';

export interface EventLogDetailLink {
  /** e.g. "Reissued from" / "Reissued as" */
  prefix: string;
  payment_id: string;
  label: string;
}

export interface EventLogEntry {
  id: string;
  event_type: string;
  from_status: string | null;
  to_status: string | null;
  created_at: string;
  actor_type: string;
  actor_id: string;
  source: string;
  /** Optional plain-text secondary line under the title. */
  detail?: string | null;
  /** Optional linked payment name (preferred over plain detail when set). */
  detail_link?: EventLogDetailLink | null;
}

const EVENT_COLORS: Record<string, string> = {
  CREATED: 'bg-green-500',
  APPROVED: 'bg-green-500',
  RELEASED: 'bg-green-500',
  REISSUED: 'bg-green-500',
  FUNDS_RECEIVED: 'bg-green-500',
  COMPLETED: COMPLETED_STATUS_STYLES.dot,
  RETURNED: 'bg-rose-500',
  UPLOADED: 'bg-green-500',
  RECEIVED: 'bg-green-500',
  PROCESSED: 'bg-green-500',
  FAILED: 'bg-red-500',
  REJECTED: 'bg-red-500',
  CANCELLED: 'bg-red-500',
  EXPIRED: 'bg-red-500',
  DELETED: 'bg-red-500',
  DISAPPROVED: 'bg-red-500',
};

function eventLabel(t: string): string {
  return t
    .replace(/^(PAYMENT_|FILE_)/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function dotColor(t: string): string {
  return EVENT_COLORS[t.replace(/^(PAYMENT_|FILE_)/, '')] ?? 'bg-amber-500';
}

function actorName(id: string): string {
  const name = id.split('@')[0].split('.')[0];
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

function entrySubtitle(e: EventLogEntry): string {
  const date = `${formatDate(e.created_at)} at ${formatTime(e.created_at)}`;
  const actor = e.actor_type === 'USER' && e.actor_id ? actorName(e.actor_id) : 'system';
  return `by ${actor} on ${date}`;
}

interface EventTimelineProps {
  fetchEventLog: () => Promise<EventLogEntry[]>;
  title?: string;
  creationLabel?: string;
  embedded?: boolean;
}

export function EventTimeline({
  fetchEventLog,
  title = 'Event History',
  creationLabel = 'Created',
  embedded = false,
}: EventTimelineProps) {
  const [entries, setEntries] = useState<EventLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Keep a ref so the latest fetchEventLog is always used without it being
  // a useEffect/useCallback dependency. This prevents inline arrow props from
  // the parent (e.g. `fetchEventLog={() => getFileEventLog(id)}`) from
  // triggering a new fetch on every parent re-render.
  const fetchEventLogRef = useRef(fetchEventLog);
  fetchEventLogRef.current = fetchEventLog;

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchEventLogRef.current();
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      setEntries([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []); // stable — intentionally no deps; ref keeps the value current

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>;

  if (error)
    return (
      <div className="py-8 text-center space-y-3">
        <p className="text-sm text-muted-foreground">No log entries to show.</p>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-3 w-3 mr-1" /> Retry
        </Button>
      </div>
    );

  if (!entries.length)
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">No event entries to show.</p>
    );

  const timeline = (
    <div className="max-h-96 overflow-auto pr-2 pt-3.5">
      <ol className="relative ml-6 border-l-2 border-muted overflow-visible">
        {entries.map((e, idx) => {
          const color = dotColor(e.event_type);
          return (
            <li key={e.id} className="mb-8 ml-6 last:mb-0">
              <span className="absolute -left-[9px] h-4 w-4">
                {idx === 0 && (
                  <span
                    className={`absolute inset-0 rounded-full ${color}`}
                    style={{ animation: 'status-pulse 1.6s infinite' }}
                  />
                )}
                <span
                  className={`relative block h-4 w-4 rounded-full ring-4 ring-background ${color}`}
                />
              </span>
              <p className="text-sm font-medium leading-tight">
                {e.from_status ? eventLabel(e.event_type) : creationLabel}
              </p>
              {e.detail_link ? (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {e.detail_link.prefix}:{' '}
                  <Link
                    to={`/payments/${e.detail_link.payment_id}`}
                    className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
                  >
                    {e.detail_link.label}
                  </Link>
                </p>
              ) : e.detail ? (
                <p className="text-xs text-muted-foreground mt-0.5">{e.detail}</p>
              ) : null}
              <p className="text-xs text-muted-foreground mt-0.5">{entrySubtitle(e)}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );

  if (embedded) return timeline;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
        <CardTitle className="text-base flex items-center gap-2">
          <ScrollText className="h-4 w-4" /> {title}
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={load} title="Refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>{timeline}</CardContent>
    </Card>
  );
}
